/**
 * Sugestão da IA em um drawer à direita da conversa. Em sombra e em sugestão a IA nunca envia
 * nada: ela gera um rascunho, e aqui o operador o vê (com o que o porteiro decidiu e as fontes) e,
 * se quiser, clica em "Aplicar sugestão" — o texto vai para a caixa de resposta, editável, e só
 * sai quando o operador apertar Enviar. Nenhuma ação é automática.
 */
import React, { useState, type ReactElement } from "react";
import { Bot } from "lucide-react";
import type { PlatformConversation } from "../../api/types.js";
import { useAiDrafts } from "../../queries.js";
import { Badge, Button, Drawer } from "../../ui/index.js";
import type { AiLiveMode } from "./aiMode.js";
import { Gate, REASON_LABEL } from "./CopilotPanel.js";

export function AiSuggestionDrawer({ conversation, aiMode, onApply }: { conversation: PlatformConversation; aiMode: AiLiveMode; onApply: (text: string) => void }): ReactElement | null {
  const [open, setOpen] = useState(false);
  const drafts = useAiDrafts(conversation.id);
  if (conversation.channel !== "whatsapp" || aiMode === "off" || aiMode === "pilot") return null;
  const latest = drafts.data?.[0];
  const text = latest?.text?.trim() ? latest.text : null;
  const reason = latest?.reason ? (REASON_LABEL[latest.reason.split(":")[0] ?? ""] ?? latest.reason) : null;
  return (
    <>
      <Button size="sm" variant={text ? "primary" : "outline"} onClick={() => setOpen(true)} data-action="open-ai-suggestion" aria-haspopup="dialog">
        <Bot className="h-4 w-4" aria-hidden focusable={false} /> Sugestão da IA
      </Button>
      <Drawer open={open} title="Sugestão da IA" onClose={() => setOpen(false)}>
        <div className="flex flex-col gap-4" data-panel="ai-suggestion">
          <p className="m-0 text-sm text-muted">
            {aiMode === "shadow"
              ? "Modo sombra: a IA só observa e gera este rascunho. Nada é enviado ao cliente."
              : "Modo sugestão: a IA propõe a resposta e você decide. Nada é enviado sem o seu clique em Enviar."}
          </p>
          {latest ? <Gate draft={latest} /> : null}
          {text ? (
            <>
              <div className="whitespace-pre-wrap rounded-control border border-dashed border-border p-3 text-sm text-ink" data-ai-suggestion-text="true">{text}</div>
              {latest?.facts.length ? (
                <p className="m-0 flex flex-wrap gap-1" aria-label="Fontes usadas">
                  {latest.facts.map((fact, index) => <Badge key={`${fact.tool}-${index}`} tone={fact.ok ? "info" : "warning"}>{`${fact.ok ? "fonte" : "sem dado"}: ${fact.tool}`}</Badge>)}
                </p>
              ) : null}
              <div className="flex flex-wrap gap-2">
                <Button variant="primary" data-action="apply-ai-suggestion" onClick={() => { onApply(text); setOpen(false); }}>Aplicar sugestão</Button>
                <Button variant="ghost" onClick={() => setOpen(false)}>Fechar</Button>
              </div>
              <p className="m-0 text-xs text-muted">Aplicar copia o texto para a caixa de resposta, onde você pode editar antes de enviar.</p>
            </>
          ) : (
            <p className="m-0 rounded-control bg-surface-soft p-3 text-sm text-muted" data-ai-suggestion="empty">
              {latest?.action === "handoff" ? `A IA não sugeriu resposta${reason ? `: ${reason}` : ""}.` : "A IA ainda não gerou sugestão para esta conversa."}
            </p>
          )}
        </div>
      </Drawer>
    </>
  );
}
